import {
	type DuplicateTaskPayload,
	type ExportNotification,
	type IkeActionTaskStatusResponse,
	IkeToken,
	type ImportTaskPayload,
	isExportTaskResult,
	type LongRunningExportType,
	LongRunningTaskStatus,
	type Notification,
	type TaskResult,
} from '@ikegps/core-api';
import type { CollectionModel } from '@ikegps/core-shared';
import { ModalService } from '../../components/modal/modal.service';
import { T, Translate } from '../../i18n';
import { Services } from '../../services';
import { AppStore } from '../../store';
import { ExportNotificationCtrl } from '../export.notification/export.notification.ctrl';
import { ExportNotificationContent, ExportNotificationTitle } from '../export.notification/export.notification';
import { CSS as ExportNotificationCSS } from '../export.notification/export.notification.style';
import { NotificationService } from '../../components/notification.toast/notification.service';
import {
	type LongRunningExport,
	type LongRunningTask,
	LongRunningTaskManagerState,
} from './long.running.task.manager.state';

const POLL_INTERVAL_MS = 5000;

/** Statuses that indicate the task has reached a terminal state */
function isTerminalStatus(status: LongRunningTaskStatus): boolean {
	return status !== LongRunningTaskStatus.RUNNING;
}
export class LongRunningTaskManagerService {
	state: LongRunningTaskManagerState;
	private completionResolvers = new Map<string, (task: LongRunningTask) => void>();

	constructor(state: LongRunningTaskManagerState) {
		this.state = state;
		this.setupBeforeUnloadWarning();
	}

	/**
	 * Returns a promise that resolves when the given task reaches a terminal state.
	 * If the task is already terminal, resolves immediately.
	 * Used by the legacy export mode to block until the export completes.
	 */
	awaitTaskCompletion(taskId: string): Promise<LongRunningTask> {
		const task = this.state.tasks.find((t) => t.taskId === taskId);
		if (task != null && isTerminalStatus(task.status)) {
			return Promise.resolve(task);
		}
		return new Promise((resolve) => {
			this.completionResolvers.set(taskId, resolve);
		});
	}

	/**
	 * Start a new long-running export task.
	 * Returns immediately after the task is created on the backend.
	 * Polling handles the rest.
	 *
	 * Units are resolved server-side from the user's DB settings.
	 */
	async startExportTask(
		exportType: LongRunningExportType,
		revisions: string[],
		exportTypeLabel: string,
		jobName: string,
		options?: { noPhotos?: boolean; silent?: boolean }
	): Promise<string> {
		const departmentId = Services.Department.current?.objectId;
		if (!departmentId) return '';

		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		const response = await Services.Department.rest.Task.create({
			type: 'export',
			payload: {
				exportType,
				revisions,
				timezone,
				noPhotos: options?.noPhotos,
			},
		});

		// Add to state immediately so the UI reflects the new task
		this.state.addExportTask({
			taskId: response.taskId,
			exportType: exportTypeLabel,
			jobName,
			status: response.status,
			startedAt: Date.now(),
			departmentId,
			silent: options?.silent,
		});

		if (!this.state.isPopoverOpen) this.state.isPulsing = true;
		if (!options?.silent) this.notifyExportStarted(exportTypeLabel, jobName);

		// Cache hit — handle the terminal response directly
		if (isTerminalStatus(response.status)) {
			await this.handleExportCompleted(response.taskId, response.status, response.result, response.notifications);
			return response.taskId;
		}

		// Still running — poll for completion
		this.startPolling(response.taskId, async (r) => {
			await this.handleExportCompleted(r.taskId, r.status, r.result, r.notifications, r.error);
		});
		return response.taskId;
	}

	/**
	 * Register a fast export (KML, JSON) in the download manager.
	 * Called before initiating the fast export HTTP request.
	 * Returns a local taskId so the caller can update the task on completion/failure.
	 */
	addFastExportTask(exportType: string, jobName: string, options?: { silent?: boolean }): string {
		const departmentId = Services.Department.current?.objectId;
		if (!departmentId) return '';

		const taskId = `fast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
		this.state.addExportTask({
			taskId,
			exportType,
			jobName,
			status: LongRunningTaskStatus.RUNNING,
			startedAt: Date.now(),
			departmentId,
			silent: options?.silent,
		});
		if (!this.state.isPopoverOpen) this.state.isPulsing = true;
		if (!options?.silent) this.notifyExportStarted(exportType, jobName);
		return taskId;
	}

	/** Mark a fast export task as completed with a download URL */
	completeFastExportTask(taskId: string, url: string, fileName: string): void {
		const taskUpdate: Partial<LongRunningExport> = {
			status: LongRunningTaskStatus.COMPLETED,
			result: { exportId: '', fileName, url },
			blobUrl: url,
			seen: false,
		};
		this.state.updateTask(taskId, taskUpdate);
		if (!this.state.isPopoverOpen) this.state.isPulsing = true;
		const exportTasks = this.state.tasks.filter((t) => LongRunningTaskManagerState.isExportTask(t));
		const exportTask = exportTasks.find((t) => t.taskId === taskId);
		if (exportTask && !exportTask.silent)
			this.notifyExportFinished(exportTask.exportType, LongRunningTaskStatus.COMPLETED);
		this.resolveCompletion(taskId);
	}

	/** Mark a fast export task as failed or completed-with-notifications */
	failFastExportTask(taskId: string, notifications?: ExportNotification[], error?: string): void {
		let status: LongRunningTaskStatus;
		let taskUpdate: Partial<LongRunningExport>;
		if (notifications != null && notifications.length > 0) {
			const hasErrors = notifications.some((n) => Array.isArray(n.errors) && n.errors.length > 0);
			status = hasErrors ? LongRunningTaskStatus.COMPLETED_ERRORS : LongRunningTaskStatus.COMPLETED_WARNINGS;
			taskUpdate = {
				status,
				notifications,
				seen: false,
				notified: this.state.isPopoverOpen,
			};
		} else {
			status = LongRunningTaskStatus.FAILED;
			taskUpdate = {
				status,
				error: error ?? Translate(T.ERROR_GENERAL),
				seen: false,
				notified: this.state.isPopoverOpen,
			};
		}
		this.state.updateTask(taskId, taskUpdate);
		if (!this.state.isPopoverOpen) this.state.isPulsing = true;
		const exportTasks = this.state.tasks.filter((t) => LongRunningTaskManagerState.isExportTask(t));
		const exportTask = exportTasks.find((t) => t.taskId === taskId);
		if (exportTask && !exportTask.silent) this.notifyExportFinished(exportTask.exportType, status);
		this.resolveCompletion(taskId);
	}

	/** Manually trigger download for a completed task */
	downloadTask(taskId: string): void {
		const exportTasks = this.state.tasks.filter((t) => LongRunningTaskManagerState.isExportTask(t));
		const exportTask = exportTasks.find((t) => t.taskId === taskId);
		const exportResult =
			exportTask?.result != null && isExportTaskResult(exportTask.result) ? exportTask.result : undefined;
		if (exportResult) {
			Services.Download.download(exportTask!.blobUrl ?? exportResult.url, exportResult.fileName);

			const taskUpdate: Partial<LongRunningExport> = { downloaded: true };
			this.state.updateTask(taskId, taskUpdate);
		}
	}

	/**
	 * Open the export notification modal for a task with warnings or errors.
	 * For warnings: user can confirm to proceed with download.
	 * For errors: informational only, no download available.
	 */
	async showNotificationModal(taskId: string): Promise<void> {
		const exportTasks = this.state.tasks.filter((t) => LongRunningTaskManagerState.isExportTask(t));
		const exportTask = exportTasks.find((t) => t.taskId === taskId);
		if (exportTask == null) return;

		const notifications = exportTask.notifications ?? [];
		if (notifications.length === 0) return;

		const ctrl = new ExportNotificationCtrl(notifications);
		const revisions = ctrl.getRevisions();

		// Fetch collections before opening the modal so content renders immediately
		const collections = (await Promise.all(revisions.map((revision) => AppStore.Collection.fetch(revision)))).filter(
			(c): c is CollectionModel => c != null
		);
		ctrl.setCollections(collections);

		const result = await ModalService.open({
			title: ExportNotificationTitle.view(ctrl),
			content: ExportNotificationContent.view(ctrl),
			hasCancel: !ctrl.errors,
			className: ExportNotificationCSS.modal,
		});

		// For warnings: if user clicks OK, proceed with download
		const exportResult =
			exportTask.result != null && isExportTaskResult(exportTask.result) ? exportTask.result : undefined;
		if (!ctrl.errors && result.ok && exportResult?.url) {
			Services.Download.download(exportTask.blobUrl ?? exportResult.url, exportResult.fileName);
		}
	}

	/**
	 * Start a new long-running import task.
	 * Returns the task ID immediately; the caller can use awaitTaskCompletion(taskId)
	 * to block until the import finishes and obtain the result.
	 */
	async startImportTask(payload: ImportTaskPayload): Promise<string> {
		const departmentId = Services.Department.current?.objectId;
		if (!departmentId) return '';

		const response = await Services.Department.rest.Task.create({
			type: 'import',
			payload,
		});

		this.state.addTask({
			taskId: response.taskId,
			status: response.status,
			startedAt: Date.now(),
			departmentId,
		});

		if (isTerminalStatus(response.status)) {
			await this.handleImportCompleted(response.taskId, response.status, response.result);
			return response.taskId;
		}

		this.startPolling(response.taskId, async (r) => {
			await this.handleImportCompleted(r.taskId, r.status, r.result, r.error);
		});
		return response.taskId;
	}

	private async handleImportCompleted(
		taskId: string,
		status: LongRunningTaskStatus,
		result?: TaskResult,
		error?: string
	): Promise<void> {
		let taskResult = result;
		if (!taskResult) {
			const response = await Services.Department.rest.Task.status({ taskId });
			taskResult = response.result;
		}
		this.state.updateTask(taskId, { status, result: taskResult, error });
		this.resolveCompletion(taskId);
	}

	/**
	 * Start a new long-running duplicate task.
	 * Returns the task ID immediately; the caller uses `awaitTaskCompletion(taskId)` to block
	 * until the duplicate finishes and read the result (success collections + notifications).
	 */
	async startDuplicateTask(payload: DuplicateTaskPayload): Promise<string> {
		const departmentId = Services.Department.current?.objectId;
		if (!departmentId) return '';

		const response = await Services.Department.rest.Task.create({
			type: 'duplicate',
			payload,
		});

		this.state.addTask({
			taskId: response.taskId,
			status: response.status,
			startedAt: Date.now(),
			departmentId,
		});

		if (isTerminalStatus(response.status)) {
			await this.handleDuplicateCompleted(response.taskId, response.status, response.result, response.notifications);
			return response.taskId;
		}

		this.startPolling(response.taskId, async (r) => {
			await this.handleDuplicateCompleted(r.taskId, r.status, r.result, r.notifications, r.error);
		});
		return response.taskId;
	}

	private async handleDuplicateCompleted(
		taskId: string,
		status: LongRunningTaskStatus,
		result?: TaskResult,
		notifications?: Notification[],
		error?: string
	): Promise<void> {
		this.state.updateTask(taskId, {
			status,
			result,
			notifications,
			error,
		});
		this.resolveCompletion(taskId);
	}

	/**
	 * Shared handler for terminal task responses.
	 * Handles COMPLETED, COMPLETED_WARNINGS, COMPLETED_ERRORS, and FAILED.
	 */
	private async handleExportCompleted(
		taskId: string,
		status: LongRunningTaskStatus,
		result?: TaskResult,
		notifications?: Notification[],
		error?: string
	): Promise<void> {
		// Prefetch the blob before updating state so the row transitions
		// to its final display status at the same time as the toast fires
		let blobUrl: string | undefined;
		const exportResult = result != null && isExportTaskResult(result) ? result : undefined;
		if (exportResult?.url) {
			blobUrl = await this.prefetchBlob(exportResult.url);
		}

		const exportTaskResult = result != null && isExportTaskResult(result) ? result : undefined;
		const taskUpdate: Partial<LongRunningExport> = {
			status,
			result: exportTaskResult,
			notifications,
			error,
			seen: false,
			notified: this.state.isPopoverOpen,
			blobUrl,
		};
		this.state.updateTask(taskId, taskUpdate);
		if (!this.state.isPopoverOpen) this.state.isPulsing = true;

		const exportTasks = this.state.tasks.filter((t) => LongRunningTaskManagerState.isExportTask(t));
		const exportTask = exportTasks.find((t) => t.taskId === taskId);
		if (exportTask && !exportTask.silent) this.notifyExportFinished(exportTask.exportType, status);
		this.resolveCompletion(taskId);
	}

	private startPolling(taskId: string, handleComplete: (response: IkeActionTaskStatusResponse) => Promise<void>): void {
		const poll = async () => {
			try {
				const response = await Services.Department.rest.Task.status({ taskId });

				if (isTerminalStatus(response.status)) {
					await handleComplete(response);
					return;
				}

				this.state.updateTask(taskId, { status: response.status });
			} catch (_err) {
				// Transient error — will retry on the next interval
			}

			// Schedule next poll
			const handle = setTimeout(poll, POLL_INTERVAL_MS);
			this.state.updateTask(taskId, { pollHandle: handle });
		};

		const handle = setTimeout(poll, POLL_INTERVAL_MS);
		this.state.updateTask(taskId, { pollHandle: handle });
	}

	/** Resolve any pending awaitTaskCompletion promise for the given taskId */
	private resolveCompletion(taskId: string): void {
		const resolver = this.completionResolvers.get(taskId);
		if (resolver) {
			const task = this.state.tasks.find((t) => t.taskId === taskId);
			if (task) resolver(task);
			this.completionResolvers.delete(taskId);
		}
	}

	private async prefetchBlob(url: string): Promise<string> {
		try {
			const response = await fetch(url);
			const blob = await response.blob();
			return URL.createObjectURL(blob);
		} catch {
			return url;
		}
	}

	private notifyExportStarted(exportType: string, jobName: string): void {
		NotificationService.success({ key: T.EXPORT_STARTED, attrs: { type: exportType, name: jobName } });
	}

	private notifyExportFinished(exportType: string, status: LongRunningTaskStatus): void {
		const attrs = { type: exportType };
		if (status === LongRunningTaskStatus.COMPLETED) {
			NotificationService.success({ key: T.EXPORT_FINISHED, attrs });
		} else if (status === LongRunningTaskStatus.COMPLETED_WARNINGS) {
			NotificationService.warn({
				key: T.EXPORT_FINISHED,
				attrs: { ...attrs, issue: Translate(T.EXPORT_FINISHED_WARNINGS) },
			});
		} else {
			NotificationService.error({
				key: T.EXPORT_FINISHED,
				attrs: { ...attrs, issue: Translate(T.EXPORT_FINISHED_ERRORS) },
			});
		}
	}

	private readonly stopNavigate = (): boolean => {
		return this.state.pendingExports > 0 || this.state.hasActiveTasks;
	};

	private setupBeforeUnloadWarning(): void {
		if (Services.Token.has(IkeToken.FEATURE_NEW_EXPORT_UI)) {
			// New UI: exports run in the background so only warn on tab close, not internal navigation
			Services.Window.window.addEventListener('beforeunload', (event) => {
				if (this.stopNavigate()) {
					event.preventDefault();
					event.returnValue = Translate(T.EXPORT_MANAGER_NAVIGATE_AWAY);
				}
			});
		} else {
			// Legacy UI: block both tab close and internal navigation while exports are active
			Services.Window.bindNavigateAway(T.EXPORT_MANAGER_NAVIGATE_AWAY, this.stopNavigate, {
				modalTitle: T.EXPORT_MANAGER_NAVIGATE_TITLE,
				modalContent: T.EXPORT_MANAGER_NAVIGATE_AWAY,
				blockNavigation: true,
			});
		}
	}
}
